/**
 * useAltitudeText — "Altitude 3 · Feature" for a component, with the labels this workspace gave the
 * item's category (Settings › Item Categories) applied. The plain rules live in lib/altitudeDisplay;
 * this adds only the workspace's labels, read from the item-categories store that Layout loads.
 */
import { useCallback } from 'react';
import { useItemCategoriesStore } from '../../stores/prioritization/itemCategoriesStore';
import { altitudeLabelsFor, altitudeTagOf, formatAltitude, type FormatAltitudeOptions } from '../../lib/altitudeDisplay';

export interface AltitudeText {
  /** "Altitude 3 · Feature", or "3 · Feature" with `short`. Null when nothing is stored. */
  format: (stored: unknown, category?: string | null, options?: Pick<FormatAltitudeOptions, 'short'>) => string | null;
  /** Just the tag: "Feature". */
  tag: (stored: unknown, category?: string | null) => string | null;
}

export function useAltitudeText(): AltitudeText {
  const categories = useItemCategoriesStore((s) => s.categories);
  const format = useCallback<AltitudeText['format']>(
    (stored, category, options) =>
      formatAltitude(stored, { category, labels: altitudeLabelsFor(categories, category), short: options?.short }),
    [categories],
  );
  const tag = useCallback<AltitudeText['tag']>(
    (stored, category) => altitudeTagOf(stored, category, altitudeLabelsFor(categories, category)),
    [categories],
  );
  return { format, tag };
}
